import { all, call, fork, put, takeLatest } from 'redux-saga/effects'
import { notification } from 'antd'
import i18n from '../lib/i18n'
import {
  FETCH_AGING_DASHBOARD,
  FETCH_AGING_TABLE,
  FETCH_AGING_ZONE_BREAKDOWN,
  FETCH_COLLECTION_DAYS,
  FETCH_SALES_TARGET,
  UPLOAD_AGING_ANALYSIS,
  UPLOAD_COLLECTION_DAYS,
  UPLOAD_SALES_TARGET,
} from '../constants/ActionsTypes'
import {
  getAgingDashboard,
  getAgingTable,
  getZoneBreakdown,
  getCollectionDays,
  getSalesTarget,
  uploadAgingAnalysis as uploadAgingAnalysisAPI,
  uploadCollectionDays as uploadCollectionDaysAPI,
  uploadSalesTarget as uploadSalesTargetAPI,
} from '../api/Analytics'
import {
  fetchAgingDashboard,
  fetchAgingDashboardSuccess,
  fetchAgingDashboardFailure,
  fetchAgingTableSuccess,
  fetchAgingTableFailure,
  fetchZoneBreakdown,
  fetchZoneBreakdownSuccess,
  fetchZoneBreakdownFailure,
  fetchCollectionDays,
  fetchCollectionDaysSuccess,
  fetchCollectionDaysFailure,
  fetchSalesTarget,
  fetchSalesTargetSuccess,
  fetchSalesTargetFailure,
  uploadAgingAnalysisSuccess,
  uploadAgingAnalysisFailure,
  uploadCollectionDaysSuccess,
  uploadCollectionDaysFailure,
  uploadSalesTargetSuccess,
  uploadSalesTargetFailure,
} from '../actions/Analytics'

function* fetchAgingDashboardRequest({ payload }) {
  try {
    const body = yield call(getAgingDashboard, payload && payload.seller)
    yield put(fetchAgingDashboardSuccess(body.data))
  } catch (error) {
    // The Axios interceptor already surfaces API error messages globally,
    // so we only record the failure in state here (no duplicate notification).
    yield put(fetchAgingDashboardFailure(error.message))
  }
}

function* fetchAgingTableRequest({ payload }) {
  try {
    const body = yield call(getAgingTable, payload)
    yield put(fetchAgingTableSuccess(body))
  } catch (error) {
    yield put(fetchAgingTableFailure(error.message))
  }
}

function* fetchZoneBreakdownRequest({ payload }) {
  try {
    const body = yield call(getZoneBreakdown, payload && payload.seller)
    yield put(fetchZoneBreakdownSuccess(body.data))
  } catch (error) {
    yield put(fetchZoneBreakdownFailure(error.message))
  }
}

function* fetchCollectionDaysRequest({ payload }) {
  try {
    const body = yield call(getCollectionDays, payload && payload.seller)
    yield put(
      fetchCollectionDaysSuccess({ data: body.data, total: body.metadata && body.metadata.total }),
    )
  } catch (error) {
    yield put(fetchCollectionDaysFailure(error.message))
  }
}

function* fetchSalesTargetRequest({ payload }) {
  try {
    const body = yield call(getSalesTarget, payload && payload.seller)
    yield put(
      fetchSalesTargetSuccess({
        data: body.data,
        companyObjective: body.metadata && body.metadata.companyObjective,
      }),
    )
  } catch (error) {
    yield put(fetchSalesTargetFailure(error.message))
  }
}

function* uploadAgingAnalysisRequest({ payload }) {
  try {
    yield call(uploadAgingAnalysisAPI, payload)
    yield put(uploadAgingAnalysisSuccess())
    notification.success({
      message: i18n.t('analytics.success_title_aging'),
      description: i18n.t('analytics.upload_success_desc'),
    })
    // Refresh the derived views with the newly uploaded data
    yield put(fetchAgingDashboard())
    yield put(fetchZoneBreakdown())
    yield put(fetchCollectionDays())
  } catch (error) {
    yield put(uploadAgingAnalysisFailure(error.message))
  }
}

function* uploadCollectionDaysRequest({ payload }) {
  try {
    yield call(uploadCollectionDaysAPI, payload)
    yield put(uploadCollectionDaysSuccess())
    notification.success({
      message: i18n.t('analytics.success_title_collection'),
      description: i18n.t('analytics.upload_success_desc'),
    })
    // Refresh the derived views with the newly uploaded data
    yield put(fetchAgingDashboard())
    yield put(fetchZoneBreakdown())
    yield put(fetchCollectionDays())
  } catch (error) {
    yield put(uploadCollectionDaysFailure(error.message))
  }
}

function* uploadSalesTargetRequest({ payload }) {
  try {
    yield call(uploadSalesTargetAPI, payload)
    yield put(uploadSalesTargetSuccess())
    notification.success({
      message: i18n.t('analytics.success_title_target'),
      description: i18n.t('analytics.upload_success_desc'),
    })
    // The target is loaded independently of the other files, so only its own view refreshes.
    yield put(fetchSalesTarget())
  } catch (error) {
    yield put(uploadSalesTargetFailure(error.message))
  }
}

export function* fetchAgingDashboardSaga() {
  yield takeLatest(FETCH_AGING_DASHBOARD, fetchAgingDashboardRequest)
}

export function* fetchAgingTableSaga() {
  yield takeLatest(FETCH_AGING_TABLE, fetchAgingTableRequest)
}

export function* fetchZoneBreakdownSaga() {
  yield takeLatest(FETCH_AGING_ZONE_BREAKDOWN, fetchZoneBreakdownRequest)
}

export function* fetchCollectionDaysSaga() {
  yield takeLatest(FETCH_COLLECTION_DAYS, fetchCollectionDaysRequest)
}

export function* fetchSalesTargetSaga() {
  yield takeLatest(FETCH_SALES_TARGET, fetchSalesTargetRequest)
}

export function* uploadAgingAnalysisSaga() {
  yield takeLatest(UPLOAD_AGING_ANALYSIS, uploadAgingAnalysisRequest)
}

export function* uploadCollectionDaysSaga() {
  yield takeLatest(UPLOAD_COLLECTION_DAYS, uploadCollectionDaysRequest)
}

export function* uploadSalesTargetSaga() {
  yield takeLatest(UPLOAD_SALES_TARGET, uploadSalesTargetRequest)
}

export default function* rootSaga() {
  yield all([
    fork(fetchAgingDashboardSaga),
    fork(fetchAgingTableSaga),
    fork(fetchZoneBreakdownSaga),
    fork(fetchCollectionDaysSaga),
    fork(fetchSalesTargetSaga),
    fork(uploadAgingAnalysisSaga),
    fork(uploadCollectionDaysSaga),
    fork(uploadSalesTargetSaga),
  ])
}
